import React, { useEffect, useMemo } from "react"
import { useRecoilValue } from "recoil"
import { useTranslation } from "react-i18next"
import { MediumText, SecondaryText, LabelText } from "@/UI"
import SettingSection from "../../shared/SettingSection"
import { userAtom } from "@/atoms/userAtom"
import { useOrgProviders } from "@/hooks/useOrgProviders"
import SettingContentWrapper from "../../shared/SettingContentWrapper"
import { isExpressProviders, isStandardDeliveryProviders } from "@/utils/providers"
import ProvidersList from "./components/ProvidersList"
import { isExpressDeliveryFilter, isStandardDeliveryFilter } from "@/utils/delivery.utils"
import { isEmpty } from "lodash"

interface IProviderSettingsProps {}

const ProviderSettings: React.FC<IProviderSettingsProps> = () => {
  const { t } = useTranslation()

  const user = useRecoilValue(userAtom)

  const {
    fetchOrganizationProviders,
    orgProviders,
    updateSingleOrganizationProvider,
    removeOrgProvider,
    providersConfig,
    isLoading,
  } = useOrgProviders()

  useEffect(() => {
    fetchOrganizationProviders()
  }, [])

  useEffect(() => {
    fetchOrganizationProviders(true)
  }, [user?.org])

  const { expressProviders, standardProviders } = useMemo(() => {
    if (!providersConfig) return { expressProviders: [], standardProviders: [] }
    const expressProviders = providersConfig.filter((p) => isExpressProviders(p.provider))
    const standardProviders = providersConfig.filter((p) => isStandardDeliveryProviders(p.provider))
    return { expressProviders, standardProviders }
  }, [providersConfig])

  return (
    <SettingSection title={t("common.couriers")}>
      <SettingContentWrapper>
        <div className="flex flex-col">
          <div className="flex justify-between">
            <div>
              <MediumText>{t("profile.ProviderSettings.providerDetails")}</MediumText>
              <SecondaryText>
                {t("profile.ProviderSettings.enableProviderDescription")}
              </SecondaryText>
            </div>
          </div>
        </div>
        <LabelText className="mt-10 font-medium">
          {t("profile.ProviderSettings.expressDelivery")}
        </LabelText>
        <ProvidersList
          id="providers-table-express"
          isLoading={isLoading}
          providers={expressProviders}
          orgProviders={orgProviders}
          updateSingleOrganizationProvider={updateSingleOrganizationProvider}
          removeOrgProvider={removeOrgProvider}
          deliveryTypesFilter={isExpressDeliveryFilter}
        />
        {!isEmpty(standardProviders) && (
          <>
            <LabelText className="mt-10 font-medium">
              {t("profile.ProviderSettings.standardDelivery")}
            </LabelText>
            <ProvidersList
              id="providers-table-standard"
              isLoading={isLoading}
              providers={standardProviders}
              orgProviders={orgProviders}
              updateSingleOrganizationProvider={updateSingleOrganizationProvider}
              removeOrgProvider={removeOrgProvider}
              deliveryTypesFilter={isStandardDeliveryFilter}
            />
          </>
        )}
      </SettingContentWrapper>
    </SettingSection>
  )
}

export default ProviderSettings
